import { YYYYMMDDToDate } from './dateUtils';
import { locationAPIService } from '../services/locationAPIService';
import { countryService } from '../services/countryService';
import { cityService } from '../services/cityService';
import { Country, City } from '../data/types/domain';

/**
 * Calculates the percentage change between two periods of data
 * @param data - The data to calculate the percentage change from
 * @param dateRange - The date range to calculate the percentage change over
 * @returns An object containing the total number of clicks and the percentage change
 */
export const calculatePercentageChange = (
  data: { click_count: number; event_date: string }[],
  dateRange: { start: Date; end: Date }
): { total: number, change: string } => {
  const midPoint = new Date((dateRange.end.getTime() + dateRange.start.getTime()) / 2);
  
  const firstPeriod = data.filter(item => {
    const itemDate = YYYYMMDDToDate(item.event_date);
    return itemDate >= dateRange.start && itemDate <= midPoint;
  });
  
  const secondPeriod = data.filter(item => {
    const itemDate = YYYYMMDDToDate(item.event_date);
    return itemDate > midPoint && itemDate <= dateRange.end;
  });

  const firstPeriodTotal = firstPeriod.reduce((sum: number, hit: { click_count: number }) => sum + hit.click_count, 0);
  const secondPeriodTotal = secondPeriod.reduce((sum: number, hit: { click_count: number }) => sum + hit.click_count, 0);

  if (firstPeriodTotal === 0) return { total: secondPeriodTotal, change: secondPeriodTotal > 0 ? '+100%' : '0%' }; 

  const denominator = firstPeriodTotal > secondPeriodTotal ? firstPeriodTotal : secondPeriodTotal;
  const percentageChange = (Math.abs(secondPeriodTotal - firstPeriodTotal) / denominator) * 100;
  return { total: secondPeriodTotal, change: `${secondPeriodTotal < firstPeriodTotal ? '-' : '+'}${Math.round(percentageChange)}%` };
};

interface LocationOption {
  type: 'country' | 'city';
  name: string;
  country?: string;
  countryId?: number;
  cityId?: number;
}

export const searchLocations = async (
  searchQuery: string,
  countries: Country[],
  cities: City[]
): Promise<LocationOption[]> => {
  if (searchQuery.length < 3) {
    return [];
  }

  const res = await locationAPIService.searchLocations(searchQuery);
  
  const countryOptions = await Promise.all(res.countries.map(async (name: string) => {
    const existingCountry = await countryService.getCountryById(
      countries.find(c => c.name.toLowerCase() === name.toLowerCase())?.id || 0
    );
    return { 
      type: 'country' as const, 
      name,
      countryId: existingCountry?.id
    };
  }));

  const cityOptions = await Promise.all(res.cities.map(async (city: any) => {
    const existingCity = await cityService.getCityById(
      cities.find(c => 
        c.name.toLowerCase() === city.name.toLowerCase() && 
        countries.find(country => country.id === c.country_id)?.name.toLowerCase() === city.country.toLowerCase()
      )?.id || 0
    );
    return { 
      type: 'city' as const, 
      name: city.name, 
      country: city.country,
      cityId: existingCity?.id
    };
  }));

  return [...countryOptions, ...cityOptions];
};

/**
 * Extracts a readable error message from various error types
 * @param err - The error object to extract message from
 * @returns A string containing the error message
 */
export const extractErrorMessage = (err: unknown): string => {
  if (err instanceof Error) {
    return err.message;
  } else if (typeof err === 'object' && err !== null && 'message' in err) {
    return String((err as any).message);
  }
  return 'An unknown error occurred';
};
